import { Link, useLocation } from "wouter";
import { useClerk } from "@clerk/react";
import {
  LayoutDashboard,
  Users,
  CalendarDays,
  Inbox,
  UserPlus,
  Building2,
  LogOut,
  ClipboardList,
} from "lucide-react";
import { LastSignInBanner } from "@/components/ui/last-signin-banner";
import { LegalFooter } from "@/components/layout/legal-footer";
import { PracticeSwitcher } from "@/components/practice/practice-switcher";
import { clearRheumHubLocalStorage } from "@/lib/local-storage-keys";

const NAV = [
  { href: "/coordinator", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { href: "/coordinator/patients", label: "Patients", icon: Users },
  { href: "/coordinator/appointments", label: "Appointments", icon: CalendarDays },
  { href: "/coordinator/queries", label: "Advice queue", icon: Inbox },
  { href: "/coordinator/invitations", label: "Patient invites", icon: UserPlus },
  { href: "/coordinator/team", label: "Team roster", icon: Users },
  { href: "/coordinator/practices", label: "My practices", icon: Building2 },
];

export function CoordinatorLayout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { signOut } = useClerk();

  const isActive = (href: string, exact?: boolean) =>
    exact ? location === href : location === href || location.startsWith(`${href}/`);

  const handleSignOut = async () => {
    clearRheumHubLocalStorage();
    await signOut({ redirectUrl: "/" });
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="w-64 bg-slate-900 text-slate-100 flex flex-col">
        <div className="px-6 py-5 border-b border-slate-800 flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-sky-500/20 text-sky-300 flex items-center justify-center">
            <ClipboardList className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">Coordinator</p>
            <p className="text-xs text-slate-400 truncate">Practice operations</p>
          </div>
        </div>

        <div className="px-3 py-3 border-b border-slate-800">
          <PracticeSwitcher manageHref="/coordinator/practices" />
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(({ href, label, icon: Icon, exact }) => {
            const active = isActive(href, exact);
            return (
              <Link
                key={href}
                href={href}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  active
                    ? "bg-sky-500/15 text-sky-200"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`}
                data-testid={`coordinator-nav-${href.replace(/\//g, "-")}`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="px-3 py-3 border-t border-slate-800">
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
            data-testid="button-coordinator-sign-out"
          >
            <LogOut className="h-4 w-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 text-xs text-amber-900">
          <strong className="font-semibold">Non-clinical portal.</strong> You can see
          scheduling, contact details and routing. Clinical content (medications,
          conditions, AI assistant, CPD) is restricted to clinicians.
        </div>
        <main className="flex-1 overflow-auto">{children}</main>
        <LegalFooter />
      </div>
    </div>
  );
}

export default CoordinatorLayout;
